import classnames from 'classnames';
import type { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ThumbsUp } from '@/core/tamagoshi/icons/ThumbsUp';
import { ThumbsUpOutline } from '@/core/tamagoshi/icons/ThumbsUpOutline';
import { Text } from '@/core/tamagoshi/Text/Text';
import {
  undoVoteReviewAsUseful,
  voteReviewAsUseful,
} from '@/productDiscovery/ArticleReviews/articleReviews.api';
import {
  UNDO_VOTE_REVIEW,
  VOTE_REVIEW,
  votedReviewSelector,
} from '@/productDiscovery/ArticleReviews/components/ReviewCardVoting/reviewCardVoting.ducks';

import { t } from './translations';

import styles from './reviewCardVoting.module.scss';

interface Props {
  reviewId: number;
  votes: number;
  className?: string;
}

export const ReviewCardVoting: FC<Props> = ({ reviewId, votes, className }) => {
  const hasVoted = useSelector(votedReviewSelector(reviewId));
  const dispatch = useDispatch();
  const finalVotes = votes + Number(hasVoted);

  const voteAsUseful = async () => {
    await voteReviewAsUseful(reviewId);
    dispatch({ type: VOTE_REVIEW, reviewId });
  };

  const undoVoteAsUseful = async () => {
    await undoVoteReviewAsUseful(reviewId);
    dispatch({ type: UNDO_VOTE_REVIEW, reviewId });
  };

  return (
    <div
      className={classnames(
        styles.container,
        className,
        { [styles.hasVoted]: hasVoted },
        { [styles.noVotes]: !finalVotes },
      )}
      onClick={hasVoted ? undoVoteAsUseful : voteAsUseful}
      onKeyPress={
        hasVoted
          ? (e) => e.key === 'Enter' && undoVoteAsUseful()
          : (e) => e.key === 'Enter' && voteAsUseful()
      }
      data-testid={hasVoted ? 'review-unvoting-button' : 'review-voting-button'}
      role="button"
      tabIndex={0}
    >
      <div className={styles.iconWrapper}>
        <ThumbsUp className={styles.thumbBackground} />
        <ThumbsUpOutline className={styles.icon} />
      </div>
      <Text className={styles.text}>{t.format({ votes: finalVotes })}</Text>
    </div>
  );
};

ReviewCardVoting.displayName = 'ReviewCardVoting';
